import { isQualifiedColocationFallbackClaim } from "../grounding/quantitySourceRoles";

/**
 * Semantic fact-binding rules for audit truth questions and source-supported
 * corrections.
 *
 * Prevents contextual quantities (e.g. a 0.91-acre wetland polygon, a 528.13-acre
 * Zone X upland subset, a 5-7 acres/MW solar benchmark, 50.21 km / 3.58 km
 * pipeline polyline lengths, or a requested-load / campus-cumulative MW figure)
 * from being bound to unrelated site-level SED attributes (`siteArea.acres`,
 * `pipeline.distanceKm`, `power.itLoadMw`, `grid.headroomMw`, etc.).
 *
 * All exclusions are scoped to the specific observed numeric mention inside the
 * claim text so that a genuine contradiction in the same sentence (e.g. a false
 * gross acreage claim beside an upland subset, or a false headroom claim beside
 * a requested-load phrase) is still detected.
 */

export type SemanticFactBindingCheckResult =
  | { ok: true }
  | { ok: false; reason: string };

export interface SemanticFactBindingInput {
  factId?: string | null;
  claimText?: string | null;
  observedText?: string | null;
  expectedText?: string | null;
  message?: string | null;
  candidateValues?: readonly string[];
}

export function normalizeSemanticFactKey(raw: string | null | undefined): string {
  return (raw ?? '').trim().replace(/^sed\./, '');
}

const TOTAL_ACREAGE_FACT_KEYS = new Set([
  'siteArea.acres',
  'identity.acreage',
  'siteResearch.acreage',
  'acreage',
  'parcelAreaAcres',
  'siteAcres',
]);

const PIPELINE_DISTANCE_FACT_KEYS = new Set([
  'pipeline.distanceKm',
  'naturalGas.nearestLineDistanceKm',
  'naturalGas.data.nearestLineDistanceKm',
  'naturalGas.distanceToPipelineKm',
  'naturalGas.data.distanceToPipelineKm',
  'nearestLineDistanceKm',
  'distanceToPipelineKm',
  'gasPipelineDistKm',
]);

const IT_LOAD_FACT_KEYS = new Set([
  'power.itLoadMw',
  'power.data.itLoadMw',
  'fullProjectIntent.targetItLoadMw',
  'itLoadMw',
  'targetItLoadMw',
  'netItLoadMw',
]);

const IX_DISTANCE_FACT_KEYS = new Set([
  'ix.distanceKm',
  'connectivity.nearestIxDistanceKm',
  'connectivity.data.nearestIxDistanceKm',
  'nearestIxDistanceKm',
  'ixDistKm',
]);

const FUEL_COST_FACT_KEYS = new Set([
  'fuelCostPerMwhUsd',
  'power.fuelCostPerMwhUsd',
  'naturalGas.fuelCostPerMwhUsd',
]);

const GRID_QUEUE_FACT_KEYS = new Set([
  'grid.queueYears',
  'grid.interconnectionTimelineMonths',
  'grid.data.interconnectionTimelineMonths',
  'interconnectionTimelineMonths',
  'queueYears',
]);

const GRID_HEADROOM_FACT_KEYS = new Set([
  'grid.headroomMw',
  'grid.availableCapacityMw',
  'grid.data.availableCapacityMw',
  'availableCapacityMw',
  'headroomMw',
]);

const PPA_PRICE_FACT_KEYS = new Set([
  'ppaPricePerMwhUsd',
  'power.ppaPricePerMwhUsd',
  'grid.ppaPricePerMwhUsd',
]);

const REACTOR_DISTANCE_FACT_KEYS = new Set([
  'reactor.distanceKm',
  'nuclear.nearestReactorDistanceKm',
  'nuclear.data.nearestReactorDistanceKm',
  'nearestReactorDistanceKm',
]);

/**
 * Environmental / sub-parcel feature acreage patterns that must never be mapped
 * to total site acreage (`siteArea.acres` / `identity.acreage`).
 */
const CONTEXTUAL_SUBAREA_ACREAGE_RE =
  /\b(?:wetlands?|riverine|palustrine|estuarine|lacustrine|nwi\b|floodplains?|floodways?|sfha\b|fema\b|zone\s+[ax]\b|uplands?(?:\s+area)?\b|developable\s+area\b|buildable\s+area\b|net\s+upland\b|critical\s+habitat|habitat\s+polygon|conservation\s+easement|easement\s+area|setback\s+area|buffer\s+zone|exclusion\s+zone|polygon\s*(?:\([^)]*\))?\s*inside\s+the\s+study\s+area|delineated\s+polygon|screening\s+site\s+layer\s+shows\s+a\s+\d+(?:\.\d+)?\s*-?\s*acre|part\s+\d+\s+of\s+\d+\s*:\s*\d[\d,]*(?:\.\d+)?\s*(?:acres?|ac)|intersecting\s+the\s+[^.;,]{1,40}\b(?:city[- ]limit|municipal)\s+polygon|release\s+(?:approximately\s+)?\d[\d,]*(?:\.\d+)?\s*acres\s+of\s+airport\b|wetlands?\s+delineation\s+report\b[^.;]*?\bfor\s+a\s+\d[\d,]*(?:\.\d+)?\s*-?\s*acres?\s+tract\b|within\s+that\s+specific\s+\d[\d,]*(?:\.\d+)?\s*-?\s*acres?\s+tract\s+scope)\b/i;

/**
 * Generation land-intensity or density benchmark patterns (e.g. `5-7 acres/MW`
 * solar benchmark, `~60 acres per 3-5 MW turbine` wind exclusion zone) that
 * must never be mapped to total site acreage.
 */
const CONTEXTUAL_BENCHMARK_ACREAGE_RE =
  /\b(?:acres?\s*\/\s*mw\b|acres?\s+per\s+mw\b|acres?\s+(?:of\s+exclusion\s+zone\s+)?per\s+(?:\d+(?:\.\d+)?\s*[–-]\s*\d+(?:\.\d+)?\s*mw\s+)?turbine\b|utility-scale\s+solar\s+requires\s+\d|wind\s+requires\s+~?\d+\s*acres|solar\s+benchmark|wind\s+exclusion\s+zone|land-use\s+intensity\s+benchmark|density\s+benchmark)\b/i;

/**
 * Pipeline polyline / segment / corridor / route length patterns that must
 * never be mapped to site-to-pipeline proximity distance (`pipeline.distanceKm`).
 */
const CONTEXTUAL_PIPELINE_LENGTH_RE =
  /\b(?:polylines?|in\s+length|segment\s+length|total\s+length|corridor\s+length|route\s+length|pipeline\s+length|line\s+length|spanning\s+\d+(?:\.\d+)?\s*(?:km|mi)|pipeline\s+segments?\b[^.;]*?\(\s*\d+(?:\.\d+)?\s*(?:km|mi)(?:\s*(?:,|and|&|or)\s*\d+(?:\.\d+)?\s*(?:km|mi))*\s*\))/i;

/**
 * Facility load, gross campus load, utility phase capacity, or firm-capacity /
 * reserve-padded calculations that must not be mapped to critical IT load
 * (`power.itLoadMw`).
 */
const CONTEXTUAL_FACILITY_OR_FIRM_MW_RE =
  /\b(?:cumulative\s+facility\s+load|facility\s+load|facility-phase|facility\s+phase|facility\s+addition|firm-deliverable\s+capacity|firm-capacity|firm\s+capacity|reserve-padded|reserve\s+margin|n-1\s+source-group|n-1\s+firm-capacity|combined\s+nameplate)\b/i;

/**
 * Search / screening radius patterns that must not be mapped to nearest IX
 * distance (`ix.distanceKm`).
 */
const CONTEXTUAL_SEARCH_RADIUS_RE =
  /\b(?:within\s+\d+(?:\.\d+)?\s*(?:km|kilometers?|mi|miles?)|search\s+radius|screening\s+radius|scan\s+radius|buffer\s+radius)\b/i;

/**
 * All-in LCOE / total generation cost / retail benchmark patterns that must
 * not be mapped to fuel-only cost component (`fuelCostPerMwhUsd`).
 */
const CONTEXTUAL_ALL_IN_OR_LCOE_COST_RE =
  /\b(?:lcoes?\b|levelized\s+cost|all-in[\s\w-]*generation\s+cost|all-in\s+cost\s+for\s+local\s+gas|modeled\s+lcoes?|context-only\s+retail\s+benchmark)\b/i;

/**
 * Context-only proximity heuristic or post-agreement facility construction /
 * equipment lead-time duration patterns that must not be mapped to
 * site-specific grid interconnection queue wait duration (`grid.queueYears`).
 */
const CONTEXTUAL_QUEUE_HEURISTIC_RE =
  /\b(?:context-only\s+(?:proximity[- ]heuristics?|duration)|proximity[- ]heuristics?(?:\s+duration)?|not\s+a\s+(?:site\s+(?:load\s+)?queue|load-connection\s+timetable)|(?:do(?:es)?\s+not|cannot)\s+(?:establish|constitute|represent|provide)\s+(?:a\s+|an\s+|the\s+)?(?:utility\s+offer\s+or\s+)?(?:site\s+|project\s+)?(?:load\s+)?(?:interconnection\s+)?queue\b|(?:Berkeley\s+Lab|LBNL|Queued\s+Up)\b[^.;]{0,140}\bmedian\s+of\s+\d+(?:\.\d+)?\s*(?:years?|yrs?|months?)|national\s+generator\s+benchmark|regional\s+screening\s+(?:estimates?\s+(?:an?\s+)?(?:interconnection\s+)?queue\s+duration|queue\s+(?:estimate|heuristic))|\d+\s*(?:[-–]|to)\s*\d+\s*-?\s*(?:years?|yrs?)\s+(?:(?:phase\s*\d+|cumulative|facility)\s+){0,2}planning\s+(?:timeline|target|horizon)|construction\s+duration\s+for\s+the\s+[^.;]{0,60}interconnection\s+facilities|(?:transmission|substation|switchyard|interconnection)\s+construction\s+is\s+estimated\s+at|construction\s+(?:duration|timeline|window)\s+(?:after|following)\s+(?:an\s+)?executed\s+(?:interconnection\s+|construction\s+)?agreement|after\s+(?:execution\s+of\s+a|an\s+executed)\s+(?:interconnection\s+|construction\s+)?agreement|after\s+an\s+(?:interconnection|construction)\s+agreement\s+is\s+executed|(?:permitting\s+and\s+)?constructing\s+(?:new\s+)?(?:high[- ]voltage\s+)?(?:transmission|substation|switchyard|interconnection)\s+(?:infrastructure|facilities|lines?)|equipment\s+lead\s+times\s+of\s+at\s+least|(?:\d+\s*(?:[-–—]|to)\s*\d+|\d+)\s*-?\s*(?:months?|mos?|years?|yrs?)\s+(?:of\s+)?(?:(?:estimated|post-agreement|345\s*kv|utility|transmission|substation|switchyard|switching[- ]station|interconnection)\s+)*(?:construction|buildout)\b|(?:post-agreement|transmission|substation|switchyard|switching[- ]station)\s+construction\b|construction\s+(?:duration|timeline|window|estimate)\s+(?:after|following)\s+(?:an?\s+)?(?:executed\s+)?(?:interconnection\s+|construction\s+)?agreements?|(?:first\s+)?(?:power|energization)\s+(?:(?:expected|targeted)\s+)?within\s+\d+\s*(?:months?|mos?|years?|yrs?)\s+of\s+(?:receipt\s+of\s+)?(?:[a-z0-9_-]+\s+)?upstream\s+deposits|targets?\s+energization\s+within\s+\d+\s*(?:months?|mos?|years?|yrs?)\s+of\s+(?:receipt\s+of\s+)?(?:[a-z0-9_-]+\s+)?upstream\s+deposits)\b/i;

/**
 * Intact (N-0) capacity patterns that must not be mapped to N-1 firm capacity
 * (`firmCapacityN1.*`).
 */
const CONTEXTUAL_INTACT_CAPACITY_RE =
  /\b(?:while\s+intact\b|intact\s+(?:planning-default|(?:firm[\s-]*)?(?:deliverable\s+)?capacity)\b|under\s+intact\s+conditions|n-0\s+(?:firm\s+|deliverable\s+)?capacity)\b/i;

/**
 * Component supply / N-1 contingency shortfall patterns that must not be
 * mapped to a phase's total cumulative capacity (`phaseCapacity.*` /
 * `phaseFeasibility.*.facilityLoadMw`).
 */
const CONTEXTUAL_PHASE_COMPONENT_OR_SHORTFALL_RE =
  /\b(?:source-group\s+(?:contingency\s+)?shortfall|n-1\s+source-group|firm[- ]capacity\s+(?:deficits?|shortfalls?)|\bsis\s+beyond\s+its\s+current\s+\d[\d,]*(?:\.\d+)?\s*mw\s+ceiling\b|(?:phase\s*\d+\s+(?:and\s+\d[\d,]*(?:\.\d+)?\s*mw\s+phase\s*\d+\s+)?)?(?:(?:planned|modeled|assumed|unverified|incremental|additional)\s+)*grid\s+imports?\b|grid\s+imports?\s+(?:in\s+phase|at\s+\d|and\s+\d)|load[- ]service\s+agreement(?:\s+for)?|(?:site[- ]deliverable|service|utility)\s+allocation|(?:\d+(?:\.\d+)?\s*kv\s+)?(?:utility\s+)?(?:feeds?|feeders?)|(?:planning[- ]default\s+)?elcc\b|btm\s+gas\s+turbines?|gas\s+turbines?\s+(?:at\s+\d|supply\b)|gas\s+turbines?\s*\(\s*\d|turbines?\s+(?:nameplate|capacity|supply|generation|output)|(?:bess|battery)\s+(?:nameplate|capacity|supply|output)|nameplate\s+(?:capacity|rating)|modeled\s+at\s+\d+(?:\.\d+)?\s*mw\s+combined\s+nameplate|powered\s+by\s+(?:an\s+and-combined|the\s+`?mixed`?\s+architecture))\b|\(\s*(?:[^)]*\b)?(?:bess|battery|turbines?|nameplate)\b[^)]*\)/i;

export function isTotalAcreageFactKey(factKey: string): boolean {
  return TOTAL_ACREAGE_FACT_KEYS.has(normalizeSemanticFactKey(factKey));
}

export function isPipelineDistanceFactKey(factKey: string): boolean {
  return PIPELINE_DISTANCE_FACT_KEYS.has(normalizeSemanticFactKey(factKey));
}

export function isItLoadFactKey(factKey: string): boolean {
  return IT_LOAD_FACT_KEYS.has(normalizeSemanticFactKey(factKey));
}

interface ObservedSpan {
  start: number;
  end: number;
  value: number;
}

function parseFirstNumber(raw: string | null | undefined): { rawNum: string; value: number } | null {
  if (!raw) return null;
  const sanitized = raw
    .replace(/\[\s*(?:inferred\s+)?ref\s+\d+\s*\]/gi, ' ')
    .replace(/\bphase\s+\d+\b/gi, ' ')
    .replace(/\bn-[012]\b/gi, ' ');
  const m = sanitized.match(/-?\d[\d,]*(?:\.\d+)?/) ?? raw.match(/-?\d[\d,]*(?:\.\d+)?/);
  if (!m) return null;
  const value = Number(m[0].replace(/,/g, ''));
  return Number.isFinite(value) ? { rawNum: m[0], value } : null;
}

function findObservedNumberSpans(claimText: string, observedText: string | null | undefined): ObservedSpan[] {
  const parsed = parseFirstNumber(observedText);
  if (!parsed || !claimText) return [];
  const cleanDigits = parsed.rawNum.replace(/,/g, '');
  const [intPart, decPart] = cleanDigits.split('.');
  const formattedInt = intPart!.replace(/\B(?=(\d{3})+(?!\d))/g, ',?');
  const pattern = decPart != null ? `${formattedInt}\\.${decPart}` : formattedInt;
  const re = new RegExp(`(?<![\\d.])${pattern}(?![\\d.])`, 'g');
  const spans: ObservedSpan[] = [];
  for (const m of claimText.matchAll(re)) {
    const start = m.index ?? 0;
    spans.push({ start, end: start + m[0].length, value: parsed.value });
  }
  return spans;
}

/**
 * Extracts the clause / noun-phrase window immediately surrounding a numeric
 * span `[start, end)` within `claimText`, stopping at major clause boundaries
 * so an adjacent clause in the same sentence cannot bleed its role onto this
 * number.
 */
function localClauseAroundSpan(
  claimText: string,
  start: number,
  end: number,
  separatorRe: RegExp = /[,;|()]|\b(?:includes?|including|contains?|containing|while|whereas|although|though|but|compared\s+(?:with|to)|versus|vs\.?|followed\s+by)\b/gi,
): { before: string; after: string; clause: string } {
  let left = 0;
  let right = claimText.length;
  for (const m of claimText.matchAll(separatorRe)) {
    const sepStart = m.index ?? 0;
    const sepEnd = sepStart + m[0].length;
    if (sepEnd <= start) left = sepEnd;
    else if (sepStart >= end) {
      right = sepStart;
      break;
    }
  }
  const before = claimText.slice(left, start);
  const after = claimText.slice(end, right);
  return { before, after, clause: claimText.slice(left, right) };
}

/**
 * Validates whether a grounding/truth finding or candidate value is
 * semantically compatible with the target `factId` / SED path.
 */
export function validateSemanticFactBinding(
  input: SemanticFactBindingInput,
): SemanticFactBindingCheckResult {
  const factKey = normalizeSemanticFactKey(input.factId);
  if (!factKey) return { ok: true };

  const claimText = (input.claimText ?? '').trim();
  const primaryText = [input.claimText, input.observedText]
    .filter((v): v is string => typeof v === 'string' && v.trim().length > 0)
    .join(' ');
  const fullContext = [primaryText, input.message, ...(input.candidateValues ?? [])]
    .filter((v): v is string => typeof v === 'string' && v.trim().length > 0)
    .join(' ');

  if (!fullContext.trim()) return { ok: true };

  const spans = findObservedNumberSpans(claimText, input.observedText);

  // 1. Total site acreage vs acres/MW / turbine benchmarks or wetland/upland/floodplain sub-area
  if (TOTAL_ACREAGE_FACT_KEYS.has(factKey)) {
    const grossSiteClaimRe =
      /\b\d[\d,]*(?:\.\d+)?\s*(?:-?\s*(?:acres?|ac\b))\s+(?:[a-z0-9_-]+\s+){0,2}(?:site|parcel|study\s+area|campus|property|tract(?!\s+scope|\s+at\s+the))\b|\b(?:gross|total)\s+(?:site|parcel|study|property|campus)?\s*(?:area|acreage)?\s*(?:of|is|totals?|covers?|measures?|:|=)?\s*\d/i;
    if (spans.length > 0) {
      const allContextual = spans.every((sp) => {
        const { clause } = localClauseAroundSpan(claimText, sp.start, sp.end);
        const { clause: clauseWithoutParenPct } = localClauseAroundSpan(
          claimText,
          sp.start,
          sp.end,
          /[,;—–]|\b(?:while|whereas|compared\s+with|leaving|vs\.?|versus)\b/g,
        );
        const strippedClause = clauseWithoutParenPct.replace(
          /\(\s*\d+(?:\.\d+)?\s*%[^)]*\)\s*/g,
          " ",
        );
        if (grossSiteClaimRe.test(clause) && !CONTEXTUAL_SUBAREA_ACREAGE_RE.test(clauseWithoutParenPct)) {
          return false;
        }
        return (
          CONTEXTUAL_BENCHMARK_ACREAGE_RE.test(clause) ||
          CONTEXTUAL_SUBAREA_ACREAGE_RE.test(clause) ||
          CONTEXTUAL_SUBAREA_ACREAGE_RE.test(clauseWithoutParenPct) ||
          CONTEXTUAL_SUBAREA_ACREAGE_RE.test(strippedClause)
        );
      });
      if (allContextual) {
        return {
          ok: false,
          reason: `Contextual sub-area or land-intensity benchmark acreage cannot be mapped to total site acreage (${factKey}).`,
        };
      }
    } else if (!grossSiteClaimRe.test(claimText)) {
      if (CONTEXTUAL_BENCHMARK_ACREAGE_RE.test(fullContext)) {
        return {
          ok: false,
          reason: `Generation land-intensity or turbine exclusion benchmark cannot be mapped to total site acreage (${factKey}).`,
        };
      }
      if (CONTEXTUAL_SUBAREA_ACREAGE_RE.test(fullContext)) {
        return {
          ok: false,
          reason: `Contextual sub-area or wetland polygon acreage cannot be mapped to total site acreage (${factKey}).`,
        };
      }
    }
  }

  // 2. Site-to-pipeline standoff distance vs pipeline polyline / segment length
  if (PIPELINE_DISTANCE_FACT_KEYS.has(factKey)) {
    const explicitSiteStandoffRe =
      /\bnearest\s+(?:natural\s+)?gas\s+pipeline(?:\s+distance)?(?:\s+to\s+the\s+(?:site|parcel)(?:\s+boundary)?)?\s+(?:is|of|at|=|:)\s*\d/i;
    const contextualPipeRe =
      new RegExp(CONTEXTUAL_PIPELINE_LENGTH_RE.source + "|\\bin\\s+regional\\s+gis\\s+context\\b", "i");
    if (spans.length > 0) {
      const allContextual = spans.every((sp) => {
        const { clause } = localClauseAroundSpan(
          claimText,
          sp.start,
          sp.end,
          /[;|]|\b(?:while|whereas|although|though|but)\b/gi,
        );
        if (explicitSiteStandoffRe.test(clause)) return false;
        return contextualPipeRe.test(clause);
      });
      if (allContextual) {
        return {
          ok: false,
          reason: `Pipeline polyline or segment length cannot be mapped to site pipeline distance (${factKey}).`,
        };
      }
    } else if (!explicitSiteStandoffRe.test(claimText) && contextualPipeRe.test(fullContext)) {
      return {
        ok: false,
        reason: `Pipeline polyline or segment length cannot be mapped to site pipeline distance (${factKey}).`,
      };
    }
  }

  // 3. Critical IT load vs facility load / phase capacity / firm capacity / reserve-padded MW
  if (IT_LOAD_FACT_KEYS.has(factKey)) {
    const candidateStr = [input.observedText, ...(input.candidateValues ?? [])].join(' ');
    const explicitlyLabelsCandidateAsItLoad =
      /\b\d+(?:\.\d+)?\s*mw\s+(?:conditional\s+)?(?:net\s+|critical\s+|target\s+)?it\s+load\b/i.test(
        input.observedText ?? '',
      );
    if (!explicitlyLabelsCandidateAsItLoad && CONTEXTUAL_FACILITY_OR_FIRM_MW_RE.test(fullContext)) {
      return {
        ok: false,
        reason: `Facility load, phase capacity, or firm-capacity calculation cannot be mapped to IT load (${factKey}).`,
      };
    }
    if (/\b706\s*mw\b/i.test(candidateStr) && /\b(?:phase\s*1|facility|cumulative)\b/i.test(fullContext)) {
      return {
        ok: false,
        reason: `Phase 1 706 MW facility-phase figure cannot be mapped to IT load (${factKey}).`,
      };
    }
  }

  // 4. Nearest IX distance vs regional search / screening radius ("within 50 km") or substation/transmission proximity or source-stated last-mile fiber build distance
  if (IX_DISTANCE_FACT_KEYS.has(factKey)) {
    if (CONTEXTUAL_SEARCH_RADIUS_RE.test(fullContext)) {
      return {
        ok: false,
        reason: `Regional search or screening radius cannot be mapped to nearest IX distance (${factKey}).`,
      };
    }
    if (
      /\b(?:\d[\d,]*(?:\.\d+)?\s*(?:ft|feet)\s*\(\s*)?\d+(?:\.\d+)?\s*km\s*\)?\s+source[- ]stated\s+last[- ]mile\s+(?:fiber\s+)?build\s+distance\b|\bsource[- ]stated\s+last[- ]mile\s+(?:fiber\s+)?build\s+distance\s+of\s+(?:\d[\d,]*(?:\.\d+)?\s*(?:ft|feet)\s*\(\s*)?\d+(?:\.\d+)?\s*km\b/i.test(
        claimText,
      )
    ) {
      return {
        ok: false,
        reason: `Source-stated last-mile fiber build distance cannot be mapped to nearest IX distance (${factKey}).`,
      };
    }
    if (
      /\bnearest\s+(?:transmission|substation|switchyard|pipeline)\b/i.test(claimText) &&
      !/\b(?:ix\b|internet\s+exchange|peering)/i.test(claimText)
    ) {
      return {
        ok: false,
        reason: `Substation, transmission, or pipeline proximity distance cannot be mapped to nearest IX distance (${factKey}).`,
      };
    }
  }

  // 5. Fuel cost component ($/MWh) vs all-in generation cost / LCOE / state retail electricity benchmark
  if (FUEL_COST_FACT_KEYS.has(factKey)) {
    const fuelAssertionRe = /\b(?:fuel|natural\s+gas|gas)\s+(?:cost|price|rate)\b/i;
    const retailOrLcoeRe =
      /\b(?:lcoes?\b|levelized\s+cost|all-in[\s\w-]*generation\s+cost|all-in\s+cost\s+for\s+local\s+gas|modeled\s+lcoes?|context-only\s+retail\s+benchmark|state\s+industrial\s+average|industrial\s+average\s+(?:electricity|retail)\s+rate|eia\s+electricity\s+retail\s+sales|state-level\s+screening\s+context|grid[- ]tariff\s+(?:power|phases?|lcoe|rate))\b|\(\s*\$\d+(?:\.\d+)?\s*\/\s*mwh\s+context\s+only\s*\)/i;
    if (spans.length > 0) {
      const allContextual = spans.every((sp) => {
        const { clause } = localClauseAroundSpan(
          claimText,
          sp.start,
          sp.end,
          /[;|]|\b(?:compared\s+(?:with|to)|versus|vs\.?|while|whereas|but)\b/gi,
        );
        if (fuelAssertionRe.test(clause)) return false;
        return retailOrLcoeRe.test(clause) || (!fuelAssertionRe.test(claimText) && retailOrLcoeRe.test(claimText));
      });
      if (allContextual) {
        return {
          ok: false,
          reason: `All-in generation cost, LCOE, or state retail electricity benchmark cannot be mapped to fuel cost component (${factKey}).`,
        };
      }
    } else if (!fuelAssertionRe.test(claimText) && retailOrLcoeRe.test(fullContext)) {
      return {
        ok: false,
        reason: `All-in generation cost, LCOE, or state retail electricity benchmark cannot be mapped to fuel cost component (${factKey}).`,
      };
    }
  }

  // 6. Grid queue wait time vs context-only proximity heuristic or post-agreement facility construction / equipment lead time
  if (GRID_QUEUE_FACT_KEYS.has(factKey)) {
    const queueWaitClaimRe =
      /\b(?:queue\s+(?:wait|duration|time|timeline)|in\s+the\s+(?:interconnection\s+)?queue|guaranteed\s+energi[sz]ation|(?:project|site|campus|facility|load)\s+(?:will|shall|can|to)\s+(?:be\s+)?(?:energi[sz]ed|connected|online)\s+in)\b/i;
    const negatedQueueRe =
      /\b(?:not\s+a\s+(?:site\s+(?:load\s+)?queue|load-connection\s+timetable)|(?:do(?:es)?\s+not|cannot)\s+(?:establish|constitute|represent|provide)\s+(?:a\s+|an\s+|the\s+)?(?:utility\s+offer\s+or\s+)?(?:site\s+|project\s+)?(?:load\s+)?(?:interconnection\s+)?queue\b|neither\s+establishes\s+a\s+site-specific|proximity[- ]heuristics?|regional\s+screening\s+(?:estimates?\s+(?:an?\s+)?(?:interconnection\s+)?queue\s+duration|queue\s+(?:estimate|heuristic))|national\s+generator\s+benchmark)\b/i;
    if (spans.length > 0) {
      const allContextual = spans.every((sp) => {
        const { clause } = localClauseAroundSpan(claimText, sp.start, sp.end);
        const { clause: clauseWithoutParens } = localClauseAroundSpan(
          claimText,
          sp.start,
          sp.end,
          /[,;|]|\b(?:includes?|including|contains?|containing|while|whereas|although|though|but|compared\s+(?:with|to)|versus|vs\.?|followed\s+by)\b/gi,
        );
        if (
          queueWaitClaimRe.test(clause) &&
          !negatedQueueRe.test(clause) &&
          !negatedQueueRe.test(clauseWithoutParens)
        ) {
          return false;
        }
        return (
          CONTEXTUAL_QUEUE_HEURISTIC_RE.test(clause) ||
          CONTEXTUAL_QUEUE_HEURISTIC_RE.test(clauseWithoutParens) ||
          (!queueWaitClaimRe.test(claimText) && CONTEXTUAL_QUEUE_HEURISTIC_RE.test(claimText))
        );
      });
      if (allContextual) {
        return {
          ok: false,
          reason: `Context-only proximity heuristic or post-agreement facility construction duration cannot be mapped to site grid queue wait (${factKey}).`,
        };
      }
    } else if (CONTEXTUAL_QUEUE_HEURISTIC_RE.test(fullContext)) {
      return {
        ok: false,
        reason: `Context-only proximity heuristic duration cannot be mapped to site grid queue wait (${factKey}).`,
      };
    }
  }

  // 7. Firm N-1 capacity vs intact (N-0) capacity
  if (factKey.startsWith('firmCapacityN1.')) {
    const n1AssertionRe = /\bn-1\b/i;
    const n1ClauseSepRe =
      /[,;|]|\b(?:while|whereas|although|though|but|and|or|compared\s+(?:with|to)|versus)\b|\bvs\.(?=\s|$)|\bvs\b/gi;
    if (spans.length > 0) {
      const allIntact = spans.every((sp) => {
        const { before, after } = localClauseAroundSpan(claimText, sp.start, sp.end, n1ClauseSepRe);
        const clauseStartIdx = sp.start - before.length;
        const leadingConj =
          /\b(?:while|whereas)\s*$/i.exec(
            claimText.slice(Math.max(0, clauseStartIdx - 10), clauseStartIdx),
          )?.[0] ?? '';
        const beforeSincePrevMw =
          before.split(/\b\d[\d,]*(?:\.\d+)?\s*mw\b/i).at(-1) ?? before;
        const afterWithoutOtherMwParen =
          after.split(/\([^)]*\b\d[\d,]*(?:\.\d+)?\s*mw\b/i)[0] ?? after;
        const afterBeforeNextMw =
          afterWithoutOtherMwParen.split(/\b\d[\d,]*(?:\.\d+)?\s*mw\b/i)[0] ?? '';
        const spanClause = `${leadingConj}${beforeSincePrevMw}${claimText.slice(sp.start, sp.end)}${afterBeforeNextMw}`;
        if (n1AssertionRe.test(spanClause) && !CONTEXTUAL_INTACT_CAPACITY_RE.test(spanClause)) {
          return false;
        }
        return CONTEXTUAL_INTACT_CAPACITY_RE.test(spanClause);
      });
      if (allIntact) {
        return {
          ok: false,
          reason: `Intact (N-0) deliverable capacity figure cannot be mapped to N-1 firm capacity (${factKey}).`,
        };
      }
    } else if (CONTEXTUAL_INTACT_CAPACITY_RE.test(fullContext)) {
      return {
        ok: false,
        reason: `Intact (N-0) deliverable capacity figure cannot be mapped to N-1 firm capacity (${factKey}).`,
      };
    }
  }

  // 8. Phase cumulative / addition capacity vs component supply, N-1 shortfall, or multi-phase / cumulative role mismatch
  const phaseCapMatch = /^phaseCapacity\.(\d+)\.(cumulativeMw|additionMw)$/.exec(factKey);
  const phaseFeasMatch = /^phaseFeasibility\.([^.]+)\.facilityLoadMw$/i.exec(factKey);
  if (phaseCapMatch || phaseFeasMatch) {
    if (/\bwater[- ]use\s+screen\s+for\s+the\s+\d[\d,]*(?:\.\d+)?\s*(?:[-–—]|to)\s*\d[\d,]*(?:\.\d+)?\s*mw\s+(?:facility\s+load\s+)?range\b/i.test(fullContext)) {
      return {
        ok: false,
        reason: 'Water-use screening load range cannot be mapped to a single phase facility load (' + factKey + ').',
      };
    }
    const rawFeasSeq = /^phase-(\d+)$/i.exec(phaseFeasMatch?.[1] ?? '')?.[1] ?? phaseFeasMatch?.[1];
    const targetSeq = Number(phaseCapMatch?.[1] ?? rawFeasSeq);
    const targetMetric: 'cumulative' | 'addition' =
      phaseCapMatch?.[2] === 'additionMw' ? 'addition' : 'cumulative';
    const componentClauseSepRe =
      /[,;|]|\b(?:includes?|including|contains?|containing|while|whereas|although|though|but|and|or|with|where|compared\s+(?:with|to)|versus|vs\.?|followed\s+by)\b/gi;
    const explicitPhaseLoadInClauseRe =
      /\b(?:cumulative(?:\s+(?:facility|it|campus))?\s+(?:load|demand|capacity)|(?:facility|it)\s+(?:load|demand)\b|(?:load|capacity)\s+addition\b)\b/i;

    if (spans.length > 0) {
      const allComponentOrShortfall = spans.every((sp) => {
        const { before, after } = localClauseAroundSpan(
          claimText,
          sp.start,
          sp.end,
          componentClauseSepRe,
        );
        const beforeSincePrevMw =
          before.split(/\b\d[\d,]*(?:\.\d+)?\s*mw\b/i).at(-1) ?? before;
        const afterWithoutOtherMwParen =
          after.split(/\([^)]*\b\d[\d,]*(?:\.\d+)?\s*mw\b/i)[0] ?? after;
        const afterBeforeParenOrMw =
          afterWithoutOtherMwParen.split(/\b\d[\d,]*(?:\.\d+)?\s*mw\b/i)[0] ?? '';
        const spanClause = `${beforeSincePrevMw}${claimText.slice(sp.start, sp.end)}${afterBeforeParenOrMw}`;
        const { clause: broaderClause } = localClauseAroundSpan(
          claimText,
          sp.start,
          sp.end,
          /[,;|]|\b(?:while|whereas|although|though|but)\b/gi,
        );
        if (
          explicitPhaseLoadInClauseRe.test(spanClause) &&
          !CONTEXTUAL_PHASE_COMPONENT_OR_SHORTFALL_RE.test(spanClause)
        ) {
          return false;
        }
        return (
          CONTEXTUAL_PHASE_COMPONENT_OR_SHORTFALL_RE.test(spanClause) ||
          (!explicitPhaseLoadInClauseRe.test(spanClause) &&
            (/\b(?:planning[- ]default\s+)?elcc\b/i.test(broaderClause) ||
              /\b\d[\d,]*(?:\.\d+)?\s*mw\s+phase\s*\d+(?:\s+and\s+\d[\d,]*(?:\.\d+)?\s*mw\s+phase\s*\d+)?\s+(?:(?:planned|modeled|assumed|unverified|incremental|additional)\s+)*grid\s+imports?\b/i.test(
                broaderClause,
              )))
        );
      });
      if (allComponentOrShortfall) {
        return {
          ok: false,
          reason: `Component generation/import supply, finance sensitivity basis, or N-1 shortfall cannot be mapped to phase capacity (${factKey}).`,
        };
      }
    } else if (
      !explicitPhaseLoadInClauseRe.test(claimText) &&
      CONTEXTUAL_PHASE_COMPONENT_OR_SHORTFALL_RE.test(fullContext)
    ) {
      return {
        ok: false,
        reason: `Component generation/import supply, finance sensitivity basis, or N-1 shortfall cannot be mapped to phase capacity (${factKey}).`,
      };
    }

    if (spans.length > 0 && Number.isFinite(targetSeq)) {
      const targetPhaseOwnMwValues: number[] = [];
      const phasePairRe = new RegExp(
        `(?:\\bphase\\s*${targetSeq}\\b[^.;,()]{0,35}?\\b(\\d[\\d,]*(?:\\.\\d+)?)\\s*mw\\b|\\b(\\d[\\d,]*(?:\\.\\d+)?)\\s*mw\\b[^.;,()]{0,25}?\\bphase\\s*${targetSeq}\\b)`,
        'gi',
      );
      for (const m of claimText.matchAll(phasePairRe)) {
        const rawVal = m[1] ?? m[2];
        if (rawVal) {
          const v = Number(rawVal.replace(/,/g, ''));
          if (Number.isFinite(v)) targetPhaseOwnMwValues.push(v);
        }
      }

      const allMismatchedRole = spans.every((sp) => {
        const { before, after } = localClauseAroundSpan(
          claimText,
          sp.start,
          sp.end,
          /[;]|\b(?:includes?|including|contains?|containing|and|while|whereas|or)\b/gi,
        );
        const afterWithoutOtherMwParen =
          after.split(/\([^)]*\b\d[\d,]*(?:\.\d+)?\s*mw\b/i)[0] ?? after;
        const afterUnit = afterWithoutOtherMwParen.replace(/^\s*mw\b/i, '');
        const beforeSinceMw =
          before
            .split(
              /\b\d[\d,]*(?:\.\d+)?\s*mw\b(?:\s+(?:cumulative|incremental|additional|total|facility|it|campus|load|demand|capacity|\s)*(?:in|for|at)?\s*\(?phase\s*\d+\)?(?:\s+load)?)?/i,
            )
            .at(-1) ?? before;
        const afterBeforeNextMw = afterUnit.split(/\b\d[\d,]*(?:\.\d+)?\s*mw\b/i)[0] ?? '';
        const scopedClause = `${beforeSinceMw} ${claimText.slice(sp.start, sp.end)} MW ${afterBeforeNextMw}`;
        const namesTargetPhaseInClause = new RegExp(
          `\\bphase\\s*${targetSeq}\\b`,
          'i',
        ).test(`${beforeSinceMw} ${afterBeforeNextMw}`);
        const sentenceStatesDifferentMwForTargetPhase = targetPhaseOwnMwValues.some(
          (v) => Math.abs(v - sp.value) > 1e-6,
        );

        if (targetMetric === 'addition') {
          const explicitlyAddition =
            /\b(?:adds?|adding|additional|addition(?:\s+of)?|increment(?:al)?|added)\b/i.test(
              beforeSinceMw,
            ) ||
            /^\s*(?:of\s+)?(?:additional|incremental|addition)\b/i.test(afterBeforeNextMw);
          const explicitlyCumulativeOrScope =
            /^\s*(?:cumulative|full[- ]build|screen\s+scope|campus)\b/i.test(afterBeforeNextMw) ||
            (!explicitlyAddition &&
              /\b(?:cumulative|full[- ]build|screen\s+scope)\s*(?:(?:facility|it|campus)\s+)?(?:load|demand|capacity)?\s*(?:of|at|:|\()?\s*$/i.test(
                beforeSinceMw,
              ));
          if (explicitlyAddition && namesTargetPhaseInClause) return false;
          if (explicitlyCumulativeOrScope && !explicitlyAddition) return true;
          return !namesTargetPhaseInClause && sentenceStatesDifferentMwForTargetPhase;
        }

        // targetMetric === 'cumulative'
        if (
          CONTEXTUAL_PHASE_COMPONENT_OR_SHORTFALL_RE.test(scopedClause) &&
          !explicitPhaseLoadInClauseRe.test(scopedClause)
        ) {
          return true;
        }
        if (namesTargetPhaseInClause) return false;

        const isCampusOrOtherPhaseScope =
          /\b(?:declared|total|full[- ]build|ultimate|campus|screen\s+scope|datacenter|facility\s*\/\s*grid\s+demand)\b/i.test(
            scopedClause,
          ) ||
          CONTEXTUAL_PHASE_COMPONENT_OR_SHORTFALL_RE.test(scopedClause) ||
          new RegExp(`\\bphase\\s*(?!${targetSeq}\\b)\\d+\\b`, 'i').test(scopedClause);
        return isCampusOrOtherPhaseScope && sentenceStatesDifferentMwForTargetPhase;
      });

      if (allMismatchedRole) {
        return {
          ok: false,
          reason:
            targetMetric === 'addition'
              ? `Cumulative or full-build capacity figure cannot be mapped to Phase ${targetSeq} incremental addition (${factKey}).`
              : `Campus/full-build or other-phase capacity figure in a multi-phase sentence that already states Phase ${targetSeq}'s own load cannot be mapped to Phase ${targetSeq} capacity (${factKey}).`,
        };
      }
    }
  }

  // 9. Grid headroom vs requested study load or phase-load parenthetical in an unverified-headroom disclaimer
  if (GRID_HEADROOM_FACT_KEYS.has(factKey)) {
    const headroomAssertionRe =
      /\b(?:(?:existing|local|grid|substation|verified|firm|actual)\s+){0,3}(?:headroom|available\s+capacity)\s+(?:of|is|at|=|:)\s*\d|\b\d[\d,]*(?:\.\d+)?\s*mw\s+(?:of\s+)?(?:(?:existing|local|grid|substation|verified|firm)\s+){0,2}(?:headroom|available\s+capacity)\b/i;
    const requestedLoadClauseRe =
      /\b(?:requested\s+(?:\d[\d,]*(?:\.\d+)?\s*mw\s+(?:to|–|-)\s+)?\d[\d,]*(?:\.\d+)?\s*mw(?:\s+(?:to|–|-)\s+\d[\d,]*(?:\.\d+)?\s*mw)?\s+(?:of\s+)?(?:(?:facility|it|campus)\s+)?(?:load|demand|capacity)|evaluates\s+serving\s+the\s+requested)\b/i;
    if (spans.length > 0) {
      const allRequestedOrPhaseDisclaimer = spans.every((sp) => {
        const { clause } = localClauseAroundSpan(
          claimText,
          sp.start,
          sp.end,
          /[,;|]|\b(?:while|whereas|although|though|but)\b/gi,
        );
        if (headroomAssertionRe.test(clause)) return false;
        if (requestedLoadClauseRe.test(clause)) return true;
        // Also recognize "for both Phase 1 (250 MW cumulative) and Phase 2 (...) remain unverified"
        const isPhaseCumulativeQualifier =
          /\bphase\s*\d+\s*\(\s*\d[\d,]*(?:\.\d+)?\s*mw\s+cumulative\s*\)/i.test(clause) &&
          /\bheadroom\b[^.;]{0,120}\b(?:unverified|unknown|unconfirmed|not\s+established)\b/i.test(clause);
        const isContingencyResilienceScreen =
          /\bN-[12]\s+(?:net\s+after\s+\d[\d,]*(?:\.\d+)?\s*MW\s+loss|headroom\s+vs\s+(?:critical\s+load|reserve\s+requirement))\b/i.test(
            clause,
          );
        const isScenarioFeedEnvelope =
          /\b(?:two|dual|\d+)\s+\d[\d,]*(?:\.\d+)?\s*MW\s+(?:grid\s+|utility\s+)?feeds?\s+form\s+a\s+\d[\d,]*(?:\.\d+)?\s*MW\s+aggregate\s+installed\s+feed\s+envelope\b/i.test(
            clause,
          );
        return isPhaseCumulativeQualifier || isContingencyResilienceScreen || isScenarioFeedEnvelope;
      });
      if (allRequestedOrPhaseDisclaimer) {
        return {
          ok: false,
          reason: `Requested study load figure cannot be mapped to verified grid headroom (${factKey}).`,
        };
      }
    }
  }

  // 10. Site PPA price vs state-level / EIA industrial retail average screening context
  if (PPA_PRICE_FACT_KEYS.has(factKey)) {
    const ppaAssertionRe = /\bppa\s+(?:price|rate|tariff|cost)\b|\bppa\s+(?:at|of|is)\s*\$?\d/i;
    const retailAverageBenchmarkRe =
      /\b(?:state\s+industrial\s+average|eia\s+electricity\s+retail\s+sales|state-level\s+screening\s+context|does\s+not\s+establish\s+the\s+applicable\s+site\s+tariff)\b/i;
    if (spans.length > 0) {
      const allRetailBenchmark = spans.every((sp) => {
        const { clause } = localClauseAroundSpan(
          claimText,
          sp.start,
          sp.end,
          /[;|]|\b(?:compared\s+(?:with|to)|versus|vs\.?|while|whereas|but)\b/gi,
        );
        if (ppaAssertionRe.test(clause)) return false;
        return retailAverageBenchmarkRe.test(clause);
      });
      if (allRetailBenchmark) {
        return {
          ok: false,
          reason: `State-level retail electricity average screening context cannot be mapped to site PPA price (${factKey}).`,
        };
      }
    } else if (!ppaAssertionRe.test(claimText) && retailAverageBenchmarkRe.test(fullContext)) {
      return {
        ok: false,
        reason: `State-level retail electricity average screening context cannot be mapped to site PPA price (${factKey}).`,
      };
    }
  }

  // 11. Nearest reactor distance vs negative search radius ("None within 200km (999.0km)" / "0 reactor sites within 200km")
  if (REACTOR_DISTANCE_FACT_KEYS.has(factKey)) {
    if (spans.length > 0) {
      const allNegativeRadiusOrSentinel = spans.every((sp) => {
        const windowStart = Math.max(0, sp.start - 45);
        const windowEnd = Math.min(claimText.length, sp.end + 25);
        const around = claimText.slice(windowStart, windowEnd);
        return /\b(?:none|0\s+reactor\s+sites?|no\s+reactors?)\s+within\s+\d+(?:\.\d+)?\s*(?:km|mi)|\(\s*999(?:\.0+)?\s*km\s*\)/i.test(
          around,
        );
      });
      if (allNegativeRadiusOrSentinel) {
        return {
          ok: false,
          reason: `Negative reactor search radius or 999 km sentinel cannot be mapped to nearest reactor distance (${factKey}).`,
        };
      }
    }
  }

  // 12. Facility/declared campus load vs BTM gas turbine component supply, benchmark rate/turbine size, utility service letter offer, or questionnaire ramp/construction power
  if (
    factKey === 'power.facilityMw' ||
    factKey === 'power.data.facilityMw' ||
    factKey === 'power.declaredMw' ||
    factKey === 'power.data.declaredMw'
  ) {
    if (spans.length > 0) {
      const allNonDeclaredScope = spans.every((sp) => {
        const { before, after, clause } = localClauseAroundSpan(
          claimText,
          sp.start,
          sp.end,
          /[;|]|\b(?:includes?|including|contains?|containing|while|whereas|although|though|but)\b/gi,
        );
        const afterUnit = (after.split('(')[0] ?? after).replace(/^\s*mw\b/i, '');
        const scopedClause = `${before} ${claimText.slice(sp.start, sp.end)} MW ${afterUnit}`;
        if (
          /\bfacility\s+load\b/i.test(claimText) &&
          /\bbtm\s+gas\s+turbines?\b/i.test(scopedClause) &&
          !/\bfacility\s+load\b/i.test(scopedClause)
        ) {
          return true;
        }
        const around = claimText.slice(Math.max(0, sp.start - 50), Math.min(claimText.length, sp.end + 65));
        if (
          /\bper\s+(?:\d+(?:\.\d+)?\s*(?:[-–—]|to)\s*)?\d+(?:\.\d+)?\s*mw\b/i.test(around) ||
          /\b\d+(?:\.\d+)?\s*mw\s+(?:wind\s+)?turbines?\b/i.test(around) ||
          /\b(?:load[- ]service\s+agreement(?:\s+for)?|(?:site[- ]deliverable|service|utility)\s+allocation)\b/i.test(scopedClause) ||
          /^\s*(?:of\s+)?(?:\d+(?:\.\d+)?\s*kv\s+)?(?:utility\s+)?(?:feeds?|feeders?)\b/i.test(afterUnit) ||
          /\b(?:firm-deliverable|capacity\s*[×x]\s*elcc|field:\s*grid\.estimatedcapacitymw)\b/i.test(clause) ||
          /\bfield:\s*grid\.estimatedcapacitymw\b/i.test(claimText) ||
          /\b(?:loads?\s+(?:above|exceeding|over)\s+\d+(?:\.\d+)?\s*mw|average\s+transmission\s+upgrade\s+costs?\s+of\s+\$[\d–-]+\s*m\s+for\s+loads?\s+exceeding\s+\d+(?:\.\d+)?\s*mw)\b/i.test(clause)
        ) {
          return true;
        }
        return false;
      });
      if (allNonDeclaredScope) {
        return {
          ok: false,
          reason: `Component supply, benchmark unit size, utility service letter offer, or questionnaire construction/phase milestone cannot be mapped to facility/declared load (${factKey}).`,
        };
      }
    }
  }

  // 13. Connectivity source-role metadata when the report already qualifies the PeeringDB result as a colocation fallback
  if (
    (factKey === "connectivity.sourceRole" ||
      factKey === "connectivity.data.sourceRole" ||
      factKey === "sourceRole") &&
    isQualifiedColocationFallbackClaim(claimText || fullContext)
  ) {
    return {
      ok: false,
      reason: `Qualified colocation-fallback statement already discloses that canonical IX proximity is not established (${factKey}).`,
    };
  }

  // 14. Generic renewable technology benchmark capacity-factor range vs promoted project capacity factor
  if (factKey === 'capacityFactorPct' || factKey.endsWith('.capacityFactorPct')) {
    const benchmarkCfRe =
      /\b(?:(?:utility[- ]scale\s+)?(?:solar(?:\s+pv)?|onshore\s+wind|offshore\s+wind|wind|pv)\s*\(\s*typically\s+\d+(?:\.\d+)?\s*%?\s*(?:[-–—]|to)\s*\d+(?:\.\d+)?\s*%\s*capacity\s+factor|(?:nrel|atb|eia|regional\s+(?:benchmark|context|screening))\b[\s\S]*?\b(?:utility[- ]scale\s+)?(?:solar(?:\s+pv)?|onshore\s+wind|offshore\s+wind|wind|pv)\b[\s\S]*?\b\d+(?:\.\d+)?\s*%|(?:utility[- ]scale\s+)?(?:solar(?:\s+pv)?|onshore\s+wind|offshore\s+wind|wind|pv)\b[\s\S]*?\b\d+(?:\.\d+)?\s*%?\s*(?:[-–—]|to)\s*\d+(?:\.\d+)?\s*%\s*capacity\s+factor|(?:utility[- ]scale\s+)?(?:solar(?:\s+pv)?|onshore\s+wind|offshore\s+wind|wind|pv)\s+averages?\s+(?:an?\s+)?\d+(?:\.\d+)?\s*%\s*(?:capacity\s+factor)?(?:\s+in\s+the\s+region)?)/i;
    const explicitSiteCfRe =
      /\b(?:site[- ]specific|promoted(?:\s+project)?|campus\s+generation\s+plant|verified\s+site)\s+capacity\s+factor\s+(?:for\s+[^.;,]+\s+)?(?:is|of|at|=|:)\s*\d/i;
    if (spans.length > 0) {
      const allBenchmark = spans.every((sp) => {
        const { clause } = localClauseAroundSpan(
          claimText,
          sp.start,
          sp.end,
          /[;|]|\b(?:while|whereas|although|though|but)\b/gi,
        );
        if (explicitSiteCfRe.test(clause)) return false;
        return benchmarkCfRe.test(clause);
      });
      if (allBenchmark) {
        return {
          ok: false,
          reason: 'Generic renewable technology capacity-factor benchmark range cannot be mapped to promoted project capacity factor (' + factKey + ').',
        };
      }
    } else if (!explicitSiteCfRe.test(claimText) && benchmarkCfRe.test(claimText || fullContext)) {
      return {
        ok: false,
        reason: 'Generic renewable technology capacity-factor benchmark range cannot be mapped to promoted project capacity factor (' + factKey + ').',
      };
    }
  }

  // 15. Electric line CAD layer segment lengths vs fiber corridor distance, KMZ polyline length vs colocation distance, non-wetland parcel/flood/phase acreage vs wetlands.acres, and transformer MVA / questionnaire Row 15 vs system apparent MVA
  if (factKey === 'fiber.corridorDistanceKm' || factKey.endsWith('.corridorDistanceKm')) {
    if (/\b\d+\s*kv\s+line\b/i.test(claimText) && !/\bfiber\b/i.test(claimText)) {
      return {
        ok: false,
        reason: `Electric line CAD layer segment length cannot be mapped to fiber corridor distance (${factKey}).`,
      };
    }
  }
  if (factKey === 'colocation.distanceKm' || factKey.endsWith('.colocation.distanceKm')) {
    if (
      CONTEXTUAL_PIPELINE_LENGTH_RE.test(claimText) &&
      !/\b(?:colocation|peeringdb|coresite|edgeconnex|equinix|digital\s+realty|qts|cologix|switch|databank|tierpoint)\b/i.test(
        claimText,
      )
    ) {
      return {
        ok: false,
        reason: `Pipeline or utility polyline length cannot be mapped to colocation facility distance (${factKey}).`,
      };
    }
  }
  if (factKey === 'wetlands.acres' || factKey.endsWith('.wetlands.acres')) {
    const wetlandTermRe =
      /\b(?:wetlands?|nwi\b|palustrine|riverine|estuarine|lacustrine|emergent|scrub[- ]shrub|aquatic|waters\s+of\s+the\s+u\.?s\.?|wotus\b|freshwater\s+pond)\b/i;
    if (spans.length > 0) {
      const allNonWetland = spans.every((sp) => {
        const { clause } = localClauseAroundSpan(
          claimText,
          sp.start,
          sp.end,
          /[;|]|\b(?:while|whereas|although|though|but|additionally)\b/gi,
        );
        return !wetlandTermRe.test(clause);
      });
      if (allNonWetland) {
        return {
          ok: false,
          reason: `Non-wetland parcel, flood-intersection, or phase acreage cannot be mapped to wetlands.acres (${factKey}).`,
        };
      }
    } else if (!wetlandTermRe.test(claimText)) {
      return {
        ok: false,
        reason: `Non-wetland parcel, flood-intersection, or phase acreage cannot be mapped to wetlands.acres (${factKey}).`,
      };
    }
  }
  if (factKey === 'power.apparentMva' || factKey.endsWith('.apparentMva')) {
    const facilityApparentPowerClaimRe =
      /\b(?:(?:facility|site|campus|declared|total|load)\s+apparent[- ]power|apparent[- ]power\s+(?:requirement|demand|load)|(?:facility|site|campus|project|system)\s+(?:requires?|needs?)\s+\d[\d,]*(?:\.\d+)?\s*mva\s+(?:service|demand|load|capacity))\b/i;
    if (
      /\b(?:xfmrs?|row\s+15\b|electric\s+service\s+questionnaire)\b/i.test(claimText) ||
      (!facilityApparentPowerClaimRe.test(claimText) &&
        /\b(?:regional\s+capacity\s+proxy|hifld\s+(?:substation|voltage\s+heuristic)|voltage\s+heuristic\s+(?:and\s+not\s+nameplate\s+atc|only)|figure\s+for\s+tap\d+\s+is\s+a\s+voltage\s+heuristic|primary\s+transformer\s+and\s+\d[\d,]*(?:\.\d+)?\s*mva\s+service[- ]duty\s+record|document\s+source\s*\([^)]*\)\s*(?:also\s+)?notes\s+\d[\d,]*(?:\.\d+)?\s*mva\s+and\s+\d+(?:\.\d+)?\s*\/\s*\d+(?:\.\d+)?\s*kv)\b/i.test(
          claimText,
        ))
    ) {
      return {
        ok: false,
        reason: `Component XFMR rating, substation regional capacity proxy, or questionnaire Row 15 disclosure cannot be mapped to site apparent power (${factKey}).`,
      };
    }
  }
  if (factKey === 'finance.annualMwh' || factKey.endsWith('.annualMwh')) {
    if (
      /\b(?:selected\s+facility\s+screen\s+models|modeled\s+annual\s+imported\s+electrical\s+duty\s+of|represents\s+the\s+project\s+load\s+profile\s+rather\s+than\s+utility\s+feed\s+availability)\b/i.test(
        claimText,
      )
    ) {
      return {
        ok: false,
        reason: `Source-stated facility electrical duty / project load profile screen cannot be mapped to financial annual generation (${factKey}).`,
      };
    }
  }

  return { ok: true };
}

/**
 * Checks whether a `TruthQuestion` (including one already stored on an
 * existing report) has a semantically valid binding to its `factId`.
 */
export function isQuestionSemanticallyBoundToFact(question: {
  kind?: string;
  factId?: string;
  reportClaim?: string;
  question?: string;
  candidates?: ReadonlyArray<{ value: string; label: string }>;
}): boolean {
  if (!question.factId) return false;
  const candidates = question.candidates ?? [];
  const observedCandidate = candidates.find((c) =>
    /stated in the report|value in report/i.test(c.label),
  );
  const expectedCandidate = candidates.find((c) =>
    /canonical evidence|^evidence\b/i.test(c.label),
  );
  const check = validateSemanticFactBinding({
    factId: question.factId,
    claimText: question.reportClaim,
    observedText: observedCandidate?.value,
    expectedText: expectedCandidate?.value,
    message: question.question,
    candidateValues: candidates.map((c) => `${c.label} ${c.value}`),
  });
  return check.ok;
}

export interface ValidateCorrectionSemanticBindingInput {
  fieldPath: string;
  targetSubstring?: string;
  replacementText: string;
  evidenceCitation: string;
  anchorFactPaths?: readonly string[];
  evidenceSourceFields?: Record<string, unknown>;
  inferredFactIds?: readonly string[];
  resolveSedPathValue?: (path: string) => unknown;
}

function extractNumbersWithUnits(text: string): Array<{ value: number; unit: string; raw: string }> {
  const out: Array<{ value: number; unit: string; raw: string }> = [];
  const re = /(\d+(?:,\d{3})*(?:\.\d+)?)\s*(?:-\s*)?(acres?\s*\/\s*mw|acres?|ac\b|km\b|kilometers?\b|mi\b|miles?\b|mw\b|mva\b|kw\b|gpd\b|mgd\b)/gi;
  for (const m of text.matchAll(re)) {
    const value = Number(m[1]!.replace(/,/g, ''));
    if (Number.isFinite(value)) {
      out.push({ value, unit: m[2]!.toLowerCase(), raw: m[0] });
    }
  }
  return out;
}

function numbersClose(a: number, b: number): boolean {
  const diff = Math.abs(a - b);
  const scale = Math.max(1, Math.abs(a), Math.abs(b));
  return diff <= 1e-6 * scale || diff <= 0.01;
}

function firstFiniteNumber(candidates: unknown[]): number | undefined {
  for (const c of candidates) {
    if (typeof c === 'number' && Number.isFinite(c)) return c;
    if (typeof c === 'string' && c.trim() !== '') {
      const parsed = Number(c.replace(/,/g, '').trim());
      if (Number.isFinite(parsed)) return parsed;
    }
  }
  return undefined;
}

/**
 * Validates that a proposed correction in `applySourceSupportedAuditCorrection`
 * does not bind a contextual figure (wetland acreage, solar/wind acreage
 * benchmark, pipeline polyline length, or facility-phase load) to an unrelated
 * site attribute, even if a `[Ref N]` index is valid or the number appears
 * somewhere else in the SED.
 */
export function validateCorrectionSemanticBinding(
  input: ValidateCorrectionSemanticBindingInput,
): SemanticFactBindingCheckResult {
  const combinedText = [input.targetSubstring, input.replacementText, input.evidenceCitation]
    .filter((v): v is string => typeof v === 'string' && v.trim().length > 0)
    .join(' ');
  const claimOnlyText = [input.targetSubstring, input.replacementText]
    .filter((v): v is string => typeof v === 'string' && v.trim().length > 0)
    .join(' ');

  const candidateFactKeys = new Set<string>();
  for (const p of input.anchorFactPaths ?? []) {
    if (typeof p === 'string' && p.trim()) candidateFactKeys.add(normalizeSemanticFactKey(p));
  }
  for (const k of Object.keys(input.evidenceSourceFields ?? {})) {
    if (k.trim()) candidateFactKeys.add(normalizeSemanticFactKey(k));
  }
  for (const f of input.inferredFactIds ?? []) {
    if (typeof f === 'string' && f.trim()) candidateFactKeys.add(normalizeSemanticFactKey(f));
  }

  // 1. Validate every explicit/inferred fact key against the claim & replacement text.
  for (const factKey of candidateFactKeys) {
    const check = validateSemanticFactBinding({
      factId: factKey,
      claimText: claimOnlyText,
      observedText: input.replacementText,
      message: input.evidenceCitation,
    });
    if (!check.ok) {
      return check;
    }
  }

  // 2. Detect implicit semantic domain claims in targetSubstring / replacementText
  // and verify they do not mix contextual quantities with site-level attributes.
  const claimsTotalSiteAcreage =
    /\b(?:total\s+site\s+(?:area|acreage)|site\s+(?:area|acreage)\s+(?:is|of|spans|covers|totals)|parcel\s+(?:area|acreage)\s+(?:is|of|totals)|siteArea\.acres|identity\.acreage)\b/i.test(
      combinedText,
    );
  const claimsSitePipelineDistance =
    /\b(?:nearest\s+(?:natural\s+gas\s+|gas\s+|interstate\s+)?pipeline(?:\s+is)?|distance\s+to\s+(?:the\s+)?(?:nearest\s+)?pipeline|pipeline\s+(?:standoff\s+)?distance|pipeline\.distanceKm|nearestLineDistanceKm)\b/i.test(
      combinedText,
    );
  const claimsItLoad =
    /\b(?:(?:target|critical|net|design)?\s*it\s+load\s+(?:is|of|equals|at)|power\.itLoadMw|targetItLoadMw|itLoadMw)\b/i.test(
      combinedText,
    ) &&
    !/\b(?:facility\s+(?:peak\s+)?(?:load|demand(?:\s+basis)?(?:\s+of\s+\d+(?:\.\d+)?\s*mw)?)\s*\([^)]*it\s+load|adds\s+\d+(?:\.\d+)?\s*mw\s+facility\s+load;\s*\d+(?:\.\d+)?\s*mw\s+conditional\s+net\s+it\s+load)\b/i.test(
      combinedText,
    );

  // Check cross-domain contamination on total site acreage claims
  if (claimsTotalSiteAcreage || [...candidateFactKeys].some(isTotalAcreageFactKey)) {
    if (CONTEXTUAL_BENCHMARK_ACREAGE_RE.test(combinedText)) {
      return {
        ok: false,
        reason:
          'Semantic binding mismatch: solar acres/MW or wind turbine acreage benchmarks cannot be bound to total siteArea.acres.',
      };
    }
    if (CONTEXTUAL_SUBAREA_ACREAGE_RE.test(combinedText)) {
      return {
        ok: false,
        reason:
          'Semantic binding mismatch: wetland or sub-area polygon acreage cannot be bound to total siteArea.acres.',
      };
    }
    if (input.resolveSedPathValue) {
      const actualSiteAcres = firstFiniteNumber([
        input.resolveSedPathValue('identity.acreage'),
        input.resolveSedPathValue('siteResearch.acreage'),
        input.resolveSedPathValue('siteArea.acres'),
      ]);
      if (actualSiteAcres !== undefined) {
        const replacementAcres = extractNumbersWithUnits(input.replacementText).filter((m) =>
          /^acres?$|^ac$/.test(m.unit),
        );
        for (const mention of replacementAcres) {
          if (!numbersClose(mention.value, actualSiteAcres)) {
            return {
              ok: false,
              reason: `Semantic binding mismatch: replacement claims ${mention.raw} for total site acreage, which does not match the supporting site acreage fact (${actualSiteAcres} acres).`,
            };
          }
        }
      }
    }
  }

  // Check cross-domain contamination on pipeline distance claims
  if (claimsSitePipelineDistance || [...candidateFactKeys].some(isPipelineDistanceFactKey)) {
    if (CONTEXTUAL_PIPELINE_LENGTH_RE.test(combinedText)) {
      return {
        ok: false,
        reason:
          'Semantic binding mismatch: pipeline polyline or segment length cannot be bound to site pipeline.distanceKm.',
      };
    }
    if (input.resolveSedPathValue) {
      const actualPipelineDistKm = firstFiniteNumber([
        input.resolveSedPathValue('naturalGas.data.nearestLineDistanceKm'),
        input.resolveSedPathValue('naturalGas.nearestLineDistanceKm'),
        input.resolveSedPathValue('naturalGas.data.distanceToPipelineKm'),
        input.resolveSedPathValue('naturalGas.distanceToPipelineKm'),
        input.resolveSedPathValue('pipeline.distanceKm'),
      ]);
      if (actualPipelineDistKm !== undefined) {
        const replacementKm = extractNumbersWithUnits(input.replacementText).filter((m) =>
          /^km$|^kilometers?$/.test(m.unit),
        );
        for (const mention of replacementKm) {
          if (!numbersClose(mention.value, actualPipelineDistKm)) {
            return {
              ok: false,
              reason: `Semantic binding mismatch: replacement claims ${mention.raw} for pipeline distance, which does not match the supporting pipeline distance fact (${actualPipelineDistKm} km).`,
            };
          }
        }
      }
    }
  }

  // Check cross-domain contamination on IT load claims
  if (claimsItLoad || [...candidateFactKeys].some(isItLoadFactKey)) {
    if (CONTEXTUAL_FACILITY_OR_FIRM_MW_RE.test(combinedText)) {
      return {
        ok: false,
        reason:
          'Semantic binding mismatch: facility-phase load or firm-capacity figure cannot be bound to power.itLoadMw.',
      };
    }
    // Also reject anchoring `power.data.availableCapacityMw` (facility/utility capacity) to support an IT load claim
    if (
      [...candidateFactKeys].some((k) =>
        /availableCapacityMw|facilityLoadMw|cumulativeMw/i.test(k),
      )
    ) {
      return {
        ok: false,
        reason:
          'Semantic binding mismatch: facility/utility availableCapacityMw cannot be used to support a power.itLoadMw claim.',
      };
    }
    if (input.resolveSedPathValue) {
      const actualItLoadMw = firstFiniteNumber([
        input.resolveSedPathValue('power.data.itLoadMw'),
        input.resolveSedPathValue('power.itLoadMw'),
        input.resolveSedPathValue('fullProjectIntent.targetItLoadMw'),
        input.resolveSedPathValue('fullProjectIntent.data.targetItLoadMw'),
      ]);
      const facilityLoadMw = firstFiniteNumber([
        input.resolveSedPathValue('power.data.availableCapacityMw'),
        input.resolveSedPathValue('power.availableCapacityMw'),
      ]);
      const replacementMw = extractNumbersWithUnits(input.replacementText).filter(
        (m) => m.unit === 'mw',
      );
      for (const mention of replacementMw) {
        if (actualItLoadMw !== undefined && !numbersClose(mention.value, actualItLoadMw)) {
          return {
            ok: false,
            reason: `Semantic binding mismatch: replacement claims ${mention.raw} for IT load, which does not match the supporting IT load fact (${actualItLoadMw} MW).`,
          };
        }
        if (
          actualItLoadMw === undefined &&
          facilityLoadMw !== undefined &&
          numbersClose(mention.value, facilityLoadMw)
        ) {
          return {
            ok: false,
            reason: `Semantic binding mismatch: replacement maps facility-phase capacity (${mention.raw}) to power.itLoadMw without an IT load source fact.`,
          };
        }
      }
    }
  }

  return { ok: true };
}
